import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar,
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip,
} from 'recharts';
import AdminLayout from '../components/AdminLayout';
import { Icon } from '../utils/icons';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const tt = { borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' };

const hexToRgba = (hex, a) => { const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16); return `rgba(${r},${g},${b},${a})`; };

function fmt(v, t) {
  if (v === null || v === undefined) return '—';
  if (t === '$') return '$' + (v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : v >= 1e3 ? Math.round(v / 1e3) + 'K' : Math.round(v));
  if (t === '%') return (v || 0) + '%';
  // 'd' = days, used by the Doorly Realty KPIs (Offer→Contract,
  // Contract→Close). One decimal place when fractional ('4.5d') so a
  // 4.5-day average reads honestly; integer otherwise ('12d').
  if (t === 'd') {
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    const rounded = Math.round(n * 10) / 10;
    return (Number.isInteger(rounded) ? rounded : rounded.toFixed(1)) + 'd';
  }
  return v.toLocaleString();
}

function KPI({ label, value, iconName, color, sub, onClick }) {
  return (
    <div onClick={onClick} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow 0.15s, transform 0.15s' }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
      <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: hexToRgba(color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={iconName} size={22} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '26px', fontWeight: '900', color: '#111827', lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>{sub}</div>}
      </div>
    </div>
  );
}

function FunnelViz({ stages }) {
  const max = Math.max(...stages.map((s) => s.count), 1);
  const colors = ['#3a6ad9', '#3a6ad9', '#acf44e', '#ffdd86'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {stages.map((s, i) => (
        <div key={s.label}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{s.label}</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{s.count.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ flex: 1, height: '10px', backgroundColor: '#f3f4f6', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{ width: `${(s.count / max) * 100}%`, height: '100%', backgroundColor: colors[i] || '#3a6ad9', borderRadius: '5px', transition: 'width 0.5s' }} />
            </div>
            {i > 0 && stages[i - 1].count > 0 && (
              <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '34px', textAlign: 'right' }}>
                {Math.round((s.count / stages[i - 1].count) * 100)}%
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DashOverview() {
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [metrics, setMetrics] = useState({});
  const [charts, setCharts] = useState({ funnel: [], volumeByMonth: [] });
  const [loading, setLoading] = useState(true);
  // ⚠️ A FAILED OVERVIEW CALL MUST NOT LOOK LIKE AN EMPTY DATABASE. The load
  // below used to swallow errors with a bare `catch {}`: metrics stayed {} and
  // every KPI rendered 0 (counts) or — (money/percent), so the page looked
  // healthy and fully loaded. A ReferenceError in /overview read as a real
  // zero for three weeks. Hold the failure and say so at the top of the page.
  const [loadError, setLoadError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [drilldown, setDrilldown] = useState(null); // { label, rows, loading }
  // Flagged loans — surfaced at the top of the overview when the deadline
  // sweep has marked any borrower as overdue past their option period.
  // deadline_flag_reasons comes back as a JS array (Postgres TEXT[]), no
  // JSON.parse needed.
  const [flaggedLoans, setFlaggedLoans] = useState([]);
  // 'all' | 'standard' | 'nc' — segmented control on the Doorly Realty
  // card. Drives the home_type query param on the overview endpoint,
  // which scopes ONLY the 5 Realty queries (the rest of the page stays
  // unfiltered so executive/marketing/partnership numbers don't shift
  // when an admin is exploring NC vs resale).
  const [realtyHomeType, setRealtyHomeType] = useState('all');
  const navigate = useNavigate();

  // Force password change modal
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePwError, setChangePwError] = useState('');
  const [changePwLoading, setChangePwLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('doorly_admin_force_pw') === '1') setChangePwOpen(true);
  }, []);

  const handleChangePw = async (e) => {
    e.preventDefault(); setChangePwError('');
    if (newPassword !== confirmPassword) { setChangePwError('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setChangePwError('Password must be at least 8 characters.'); return; }
    setChangePwLoading(true);
    try { await axios.put(`${API}/api/admin/change-password`, { currentPassword, newPassword }, { headers }); localStorage.removeItem('doorly_admin_force_pw'); setChangePwOpen(false); }
    catch (err) { setChangePwError(err?.response?.data?.error || 'Failed to change password.'); }
    finally { setChangePwLoading(false); }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (realtyHomeType && realtyHomeType !== 'all') params.set('home_type', realtyHomeType);
      const res = await axios.get(`${API}/api/admin/overview?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setMetrics(res.data.metrics || {});
      setCharts(res.data.charts || { funnel: [], volumeByMonth: [] });
      setLoadError('');
    } catch (err) {
      setMetrics({});
      setCharts({ funnel: [], volumeByMonth: [] });
      setLoadError(err?.response?.data?.error || err?.message || 'Failed to load overview.');
    }
    setLoading(false);
  }, [token, startDate, endDate, realtyHomeType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  // Flagged loans — independent of the date-range filter (a flagged
  // loan must surface regardless of when the admin scoped the view).
  useEffect(() => {
    axios.get(`${API}/api/admin/flagged-loans`, { headers })
      .then((r) => setFlaggedLoans(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFlaggedLoans([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDrilldown = async (metricKey, label) => {
    setDrilldown({ label, rows: [], loading: true });
    try {
      const res = await axios.get(`${API}/api/admin/overview/drilldown?metric=${metricKey}`, { headers });
      setDrilldown({ label, rows: res.data.rows || [], loading: false });
    } catch { setDrilldown(prev => prev ? { ...prev, loading: false } : null); }
  };

  if (loading) return (
    <AdminLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#9ca3af', fontSize: '14px' }}>Loading...</div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Overview</h1>
            <p style={{ fontSize: '13px', color: '#6b7280' }}>Executive snapshot — all data from live database</p>
          </div>
          {/* Single nowrap row; date inputs flex-shrink to fit on mobile.
              WebkitAppearance:'none' removes iOS Safari's grey native date box. */}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'nowrap', minWidth: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>From</span>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              onKeyDown={e => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', fontSize: 13, color: '#111827', background: '#ffffff', WebkitAppearance: 'none', appearance: 'none', minWidth: 0, flex: 1, maxWidth: 140, fontFamily: 'inherit' }} />
            <span style={{ fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              onKeyDown={e => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', fontSize: 13, color: '#111827', background: '#ffffff', WebkitAppearance: 'none', appearance: 'none', minWidth: 0, flex: 1, maxWidth: 140, fontFamily: 'inherit' }} />
            {(startDate || endDate) && (
              <button onClick={() => { setStartDate(''); setEndDate(''); }}
                style={{ padding: '5px 10px', background: '#f3f4f6', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#374151', flexShrink: 0 }}>Clear</button>
            )}
          </div>
        </div>

        {/* ── Overview load failure ── Shown INSTEAD of trusting the tiles.
            Every KPI below reads from `metrics`, which is {} after a failed
            load, so without this banner a broken endpoint is indistinguishable
            from a business with no applications. */}
        {loadError && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '10px',
            padding: '14px 18px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '14px', color: '#dc2626' }}>
                Couldn't load overview metrics
              </div>
              <div style={{ fontSize: '12px', color: '#991b1b', marginTop: '3px' }}>
                {loadError} — the numbers below are not real; they are placeholders for data that never arrived.
              </div>
            </div>
            <button onClick={loadData} style={{
              background: '#dc2626', color: '#fff', border: 'none', padding: '7px 16px',
              borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
            }}>Retry</button>
          </div>
        )}

        {/* ── Flagged Files ── Top-of-page red banner for loans the
            deadline sweep marked as overdue past option period. */}
        {flaggedLoans.length > 0 && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '10px',
            padding: '16px 20px',
            marginBottom: '24px',
          }}>
            <div style={{
              fontWeight: 700, fontSize: '15px', color: '#dc2626',
              marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              🚩 Flagged Files ({flaggedLoans.length})
            </div>
            {flaggedLoans.map((loan) => {
              const reasons = Array.isArray(loan.deadline_flag_reasons) ? loan.deadline_flag_reasons : [];
              const flaggedAt = loan.deadline_flagged_at
                ? new Date(loan.deadline_flagged_at).toLocaleDateString()
                : '—';
              return (
                <div key={loan.id} style={{
                  background: 'white', borderRadius: '8px', padding: '12px 16px',
                  marginBottom: '8px', border: '1px solid #fecaca',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>
                      {loan.first_name} {loan.last_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      Flagged {flaggedAt} · {loan.application_status}
                    </div>
                    {reasons.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                        Overdue: {reasons.join(', ')}
                      </div>
                    )}
                  </div>
                  <a
                    href={`/admin/borrower/${loan.id}`}
                    style={{
                      background: '#dc2626', color: 'white', padding: '6px 14px',
                      borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                      textDecoration: 'none',
                    }}
                  >
                    View File →
                  </a>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Executive ── */}
        <div style={{ background: '#edf1fb', border: '1px solid #c5d3f5', borderRadius: '16px', padding: '20px', marginBottom: '20px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#3a6ad9', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>Executive</div>
          <div className="admin-mobile-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
            <KPI label="Total Applications" value={metrics.totalApplications || 0} iconName="document" color="#3a6ad9" onClick={() => openDrilldown('totalApplications', 'Total Applications')} />
            <KPI label="Total Approvals" value={metrics.totalApprovals || 0} iconName="check-circle" color="#acf44e" onClick={() => openDrilldown('approvalRate', 'Approved Loans')} />
            <KPI label="Approval Rate" value={fmt(metrics.approvalRate, '%')} iconName="chart" color="#acf44e" />
            <KPI label="Total Closings" value={metrics.totalClosings || 0} iconName="check-circle" color="#2d6a0a" onClick={() => openDrilldown('conversionRate', 'Closed Loans')} />
            <KPI label="Conversion Rate" value={fmt(metrics.conversionRate, '%')} iconName="chart" color="#3a6ad9" />
            <KPI label="Avg Deal Size" value={fmt(metrics.avgDealSize, '$')} iconName="home" color="#3a6ad9" onClick={() => openDrilldown('avgDealSize', 'Approved Loans — Deal Size')} />
            <KPI label="Realized Revenue" value={fmt(metrics.realizedRevenue, '$')} iconName="currency" color="#2d6a0a" onClick={() => openDrilldown('realizedRevenue', 'Closed Loans — Revenue')} />
            <KPI label="Funded Volume" value={fmt(metrics.fundedVolume, '$')} iconName="currency" color="#3a6ad9" onClick={() => openDrilldown('fundedVolume', 'Funded Loans')} />
            <KPI label="Deal Margin" value={metrics.dealMargin ? metrics.dealMargin + '%' : '—'} iconName="chart" color="#2d6a0a" />
            <KPI label="Early Payment Default" value={metrics.epdRate ? metrics.epdRate + '%' : '—'} iconName="warning" color="#ff082f" sub="First 6 months" />
          </div>
        </div>

        {/* ── Doorly Realty ── */}
        {/* Realty-side KPIs — acquisition timing, list-price relationship,
            and brokerage revenue. Backend derives commission at 2.5% of
            doorly_purchase_price (the A-B acquisition price, where
            Doorly's realtors earn the buyer-side commission) until a real
            commissions ledger exists. Avg Commission + Realty Revenue
            stay null until the first closed loan; the date-based KPIs
            populate as soon as a borrower has both offer-submitted and
            offer-accepted timestamps. */}
        <div style={{ background: '#edf1fb', border: '1px solid #c5d3f5', borderRadius: '16px', padding: '20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#3a6ad9', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Doorly Realty</div>
            {/* Home-type segmented control — scopes the 6 Realty metrics
                only (server applies home_type to those queries; other
                sections stay full-pipeline). Three states match the
                home_type param backend accepts: 'all' = no filter, 'standard'
                = resale, 'nc' = new construction. */}
            <div role="tablist" aria-label="Home type filter"
              style={{ display: 'inline-flex', background: '#fff', border: '1px solid #c5d3f5', borderRadius: 8, padding: 2 }}>
              {[
                { id: 'all',      label: 'All' },
                { id: 'standard', label: 'Standard' },
                { id: 'nc',       label: 'New Construction' },
              ].map((opt) => {
                const active = realtyHomeType === opt.id;
                return (
                  <button
                    key={opt.id}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setRealtyHomeType(opt.id)}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 6,
                      border: 'none',
                      background: active ? '#3a6ad9' : 'transparent',
                      color: active ? '#fff' : '#374151',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="admin-mobile-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }}>
            <KPI label="Offer Acceptance Rate" value={metrics.offerAcceptanceRate != null ? metrics.offerAcceptanceRate + '%' : '—'} iconName="check-circle" color="#acf44e" sub="Accepted ÷ submitted offers" />
            <KPI label="Offer → Contract"   value={fmt(metrics.avgOfferToContractDays, 'd')} iconName="document"     color="#3a6ad9" sub="Avg days from offer submitted to acceptance" />
            <KPI label="Contract → Close"   value={fmt(metrics.avgContractToCloseDays, 'd')} iconName="check-circle" color="#3a6ad9" sub="Avg days from acceptance to closed" />
            <KPI label="Acquisition vs. List" value={metrics.avgPriceVsListPct != null ? metrics.avgPriceVsListPct + '%' : '—'} iconName="chart"        color="#2d6a0a" sub="Doorly price ÷ list price" />
            <KPI label="Avg Commission"     value={fmt(metrics.avgRealtyCommission, '$')}    iconName="currency"     color="#acf44e" sub="Buyer-side @ 2.5% (derived)" />
            <KPI label="Realty Revenue"     value={fmt(metrics.realtyRevenue, '$')}          iconName="currency"     color="#2d6a0a" sub="Sum of commissions, closed deals" />
          </div>
        </div>

        {/* ── Marketing & Client Experience ── */}
        <div style={{ background: '#edf1fb', border: '1px solid #c5d3f5', borderRadius: '16px', padding: '20px', marginBottom: '20px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#3a6ad9', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>Marketing & Client Experience</div>
          <div className="admin-mobile-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
            <KPI label="Registered Users" value={metrics.registeredUsers || 0} iconName="users" color="#3a6ad9" onClick={() => openDrilldown('registeredUsers', 'Registered Users')} />
            <KPI label="CPL" value="—" iconName="currency" color="#6b7280" sub="Coming soon" />
            <KPI label="CAC" value="—" iconName="currency" color="#6b7280" sub="Coming soon" />
            <KPI label="Avg NPS Score" value={metrics.avgNPS || '—'} iconName="star" color={metrics.avgNPS && parseFloat(metrics.avgNPS) >= 8 ? '#2d6a0a' : '#f59e0b'} />
            <KPI label="Avg Rating" value={metrics.avgRating ? `${metrics.avgRating}/5` : '—'} iconName="star" color="#3a6ad9" />
          </div>
        </div>

        {/* ── Partnerships ── */}
        <div style={{ background: '#edf1fb', border: '1px solid #c5d3f5', borderRadius: '16px', padding: '20px', marginBottom: '28px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#3a6ad9', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>Partnerships</div>
          <div className="admin-mobile-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
            <KPI label="Pending Partners" value={metrics.pendingPartners || 0} iconName="users" color="#f59e0b" />
            <KPI label="Active Partners" value={metrics.activePartners || 0} iconName="users" color="#acf44e" onClick={() => openDrilldown('activePartners', 'Active Partners')} />
            <KPI label="Total Referrals" value={metrics.totalReferrals || 0} iconName="users" color="#3a6ad9" onClick={() => openDrilldown('totalReferrals', 'Total Referrals')} />
            <KPI label="Referred Volume" value={fmt(metrics.referredVolume, '$')} iconName="currency" color="#3a6ad9" />
            <KPI label="Closed Volume" value={fmt(metrics.closedRefVolume, '$')} iconName="currency" color="#2d6a0a" />
          </div>
        </div>

        {/* Charts */}
        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>Application Funnel</h3>
            {charts.funnel?.length > 0 && charts.funnel.some(s => s.count > 0) ? <FunnelViz stages={charts.funnel} /> : (
              <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>No data yet</div>
            )}
          </div>

          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Loan Volume by Month</h3>
            {charts.volumeByMonth?.length > 0 && charts.volumeByMonth.some(m => m.volume > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={charts.volumeByMonth} margin={{ top: 0, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${Math.round(v / 1e3)}K` : `$${v}`} />
                  <Tooltip contentStyle={tt} formatter={(v) => [fmt(v, '$'), 'Volume']} />
                  <Bar dataKey="volume" fill="#3a6ad9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>No closed loans yet — chart will populate as loans close</div>}
          </div>
        </div>
      </div>

      {/* KPI Drilldown Modal */}
      {drilldown && (
        <div onClick={() => setDrilldown(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '700px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#111827' }}>{drilldown.label}</h2>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>{drilldown.rows?.length || 0} records</p>
              </div>
              <button onClick={() => setDrilldown(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#9ca3af', lineHeight: 1 }}>x</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {drilldown.loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Loading...</div>
              ) : drilldown.rows.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No records found.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      {Object.keys(drilldown.rows[0]).filter(k => !['id','user_id'].includes(k)).slice(0, 6).map(k => (
                        <th key={k} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                          {k.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {drilldown.rows.map((row, i) => (
                      <tr key={row.id || i} style={{ borderBottom: '1px solid #f3f4f6', cursor: row.id && !drilldown.label.includes('Partner') && !drilldown.label.includes('Referral') ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (row.id && !drilldown.label.includes('Partner') && !drilldown.label.includes('Referral')) {
                            setDrilldown(null);
                            navigate(`/admin/borrowers/${row.id}`);
                          }
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        {Object.entries(row).filter(([k]) => !['id','user_id'].includes(k)).slice(0, 6).map(([k, v]) => (
                          <td key={k} style={{ padding: '10px 14px', color: '#374151', whiteSpace: 'nowrap' }}>
                            {v === null || v === undefined ? '—'
                              : k.includes('price') || k.includes('amount') || k.includes('revenue') ? fmt(parseFloat(v), '$')
                              : k.includes('_at') || k === 'created_at' || k === 'status_updated_at' ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : k === 'application_status' ? String(v).replace(/_/g, ' ')
                              : String(v)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Force Password Change Modal */}
      {changePwOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Change Your Password</h2>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>You must set a new password before continuing.</p>
            <form onSubmit={handleChangePw} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { label: 'Current Password', value: currentPassword, set: setCurrentPassword },
                { label: 'New Password', value: newPassword, set: setNewPassword },
                { label: 'Confirm New Password', value: confirmPassword, set: setConfirmPassword },
              ].map(({ label, value, set }) => (
                <div key={label}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>{label}</label>
                  <input type="password" value={value} onChange={(e) => set(e.target.value)} required
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
              ))}
              {changePwError && <p style={{ fontSize: '13px', color: '#ff082f', margin: 0 }}>{changePwError}</p>}
              <button type="submit" disabled={changePwLoading}
                style={{ backgroundColor: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '15px', fontWeight: '600', cursor: changePwLoading ? 'not-allowed' : 'pointer', opacity: changePwLoading ? 0.7 : 1 }}>
                {changePwLoading ? 'Saving...' : 'Set New Password'}
              </button>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
